import React, { Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";
import styled from "styled-components";

import { defined } from "@formant/core/common/defined";
import { IDevice } from "@formant/core/model/IDevice";

import {
    appStore,
    deviceConfigurationStore,
    observeStore,
    onlineDevicesStore,
    operateStore,
    routerStore,
} from "../../stores";
import { createTeleopShareUrl } from "../../utils/createTeleopShareUrl";
import { hashObservable } from "../../utils/hashObservable";
import { teleopShareRequest } from "../../utils/teleopShare/teleopShareRequest";
import { ShareCodeCreated } from "../Channel/ShareFlow/ShareCodeCreated";
import { Button } from "../common/Button";
import { Header } from "../common/Header";
import { Logo } from "../common/Logo";
import { StandardButton } from "../common/StandardButton";
import { TextButton } from "../common/TextButton";
import { DeviceStatus } from "../DeviceStatus";
import { ExitButton } from "./ExitButton";
import styles from "./TeleopDeviceHeader.scss";

export interface ITeleopDeviceHeaderProps {
    device: IDevice;
    offline?: boolean;
}

export const ButtonWrapper = styled.div`
    flex-grow: unset;
    display: grid;
    grid-auto-flow: column;
    grid-gap: 0.625rem;
    align-items: center;
    > button,
    > a {
        width: 7rem;
        display: inline-block;
        text-align: center;
    }
`;

@observer
export class TeleopDeviceHeader extends Component<ITeleopDeviceHeaderProps> {
    @hashObservable()
    private filterVisible: boolean = false;

    @observable
    private shareUrl?: string;

    @observable
    private willExitSoon: boolean = false;

    async componentDidMount() {
        const { device } = this.props;
        const { share } = operateStore;
        const key = "first-teleop-" + defined(device.id);
        const firstTime = localStorage.getItem(key);
        const deviceId = defined(device.id);
        let hasViews = false;
        if (device.desiredConfigurationVersion) {
            const document = await deviceConfigurationStore.fetchGet(
                deviceId,
                device.desiredConfigurationVersion,
            );
            const view = document.document.teleop?.views;
            if (view && view[0] && view[0].streamName) {
                hasViews = true;
            }
        }
        if (!firstTime && !hasViews && !appStore.preview && !share) {
            localStorage.setItem(key, "true");
            this.onConfigure();
        }
    }

    public render() {
        const { device, offline } = this.props;
        const { shareUrl, willExitSoon } = this;
        const { share } = operateStore;
        const id = defined(device.id);
        const online = onlineDevicesStore.isOnline(id) || false;

        return (
            <>
                <Header
                    title={device.name}
                    className={classnames(
                        styles["view-device-header"],
                        this.filterVisible && styles["filter-visible"],
                        observeStore.fullscreen && styles.fullscreen,
                    )}
                    hideMenu={share !== undefined}
                    preTitle={
                        share && (
                            <Button
                                to="https://formant.io"
                                className={styles["logo-button"]}
                            >
                                <Logo className={styles.logo} />
                            </Button>
                        )
                    }
                >
                    {!share && <DeviceStatus id={id} online={online} />}
                    <div className={styles["middle-region"]}>
                        {willExitSoon ? (
                            <span className={styles.warning}>
                                Your teleop session is about to end. Move your
                                device to a safe location.
                            </span>
                        ) : (
                            <>
                                <TextButton to="https://formant.io/pricing">
                                    Upgrade Formant
                                </TextButton>{" "}
                                for unlimited teleop sessions.
                            </>
                        )}
                    </div>

                    <ButtonWrapper>
                        {!share && (
                            <>
                                {!offline && (
                                    <StandardButton
                                        lighter={true}
                                        primary={true}
                                        large={false}
                                        onClick={this.onConfigure}
                                    >
                                        Customize
                                    </StandardButton>
                                )}
                                <StandardButton
                                    lighter={true}
                                    primary={true}
                                    large={false}
                                    onClick={this.onShare}
                                >
                                    Share
                                </StandardButton>
                            </>
                        )}
                        <ExitButton
                            onExit={this.onExit}
                            onWillExitSoon={this.onWillExitSoon}
                        />
                    </ButtonWrapper>
                </Header>
                {appStore.sharing && shareUrl && (
                    <ShareCodeCreated
                        url={shareUrl}
                        message="1-hour share link to your teleop view"
                        onClose={this.onShareClose}
                    />
                )}
            </>
        );
    }

    onWillExitSoon = (willExit: boolean) => {
        this.willExitSoon = willExit;
    };

    onShareClose = () => {
        appStore.sharing = false;
        this.shareUrl = undefined;
    };

    private onExit = async (timedOut: boolean) => {
        const { navigate, routeTo } = routerStore;
        const { session, share } = operateStore;
        if (share || appStore.preview) {
            window.location.href = "https://formant.io/pricing";
        } else {
            const id = session?.deviceId;
            if (id) {
                if (timedOut) {
                    appStore.setConfirm({
                        title: "Time's up!",
                        message:
                            "Upgrade Formant for unlimited teleop sessions.",
                        cancelCopy: "Go to dashboard",
                        okCopy: "Upgrade Formant",
                        onOk: () => {
                            window.location.href = "https://formant.io/pricing";
                        },
                        onCancel: async () => {
                            await navigate(routeTo.device({ id }));
                        },
                    });
                } else {
                    await navigate(routeTo.device({ id }));
                }
            }
        }
    };

    onShare = async () => {
        const { device } = this.props;
        appStore.sharing = true;

        const deviceId = defined(device.id);
        this.shareUrl = await createTeleopShareUrl(
            deviceId,
            teleopShareRequest(deviceId, new Date()),
        );
    };

    onConfigure = () => {
        operateStore.editingDevice = true;
    };
}
